import { Popover, Tree } from "@salesforce/design-system-react";
import { Button } from "@vimeo/iris/components";
import { Folder as FolderIcon, ChevronDown } from "@vimeo/iris/icons";
import { useEffect, useState } from "react";

import { Folder, FolderMetadata, ListResponse } from "vimeo";

import { client as vimeoClient } from "../vimeoClient";

interface Props {
    folderSelected: (
        uri: string,
        metadata?: FolderMetadata,
        rootFolder?: boolean
    ) => void;
    width?: "xx-small" | "x-small" | "small" | "medium" | "large";
}

interface Node {
    id: string;
    // Label is undefined only for the root node
    // with the id 0.
    label?: string;
    expanded?: boolean;
    selected?: boolean;
    type?: "item" | "branch";
    nodes?: string[];
}

interface NodeExpandEvent {
    expand: boolean;
    select: boolean;
    node: Node;
}

interface NodeClickEvent {
    select: boolean;
    node: Node;
}

// This is a special folder ID.
// The ID's of the tree items in this component
// map to URIs that we will use to make a request
// to when the user clicks the item. In this app's
// API backend, this path internally maps to the
// current team's root folder path. See that route
// for more details.
const homeFolderId = "/user/me/folders/root";

export function Folders(props: Props) {
    const [folders, setFolders] = useState<Folder[]>([]);
    const [nodes, setNodes] = useState<Record<string, Node>>({
        "0": { id: "0", nodes: [] },
    });
    const [selectedNode, setSelectedNode] = useState<Node>();

    useEffect(() => {
        async function getFolders() {
            let page = 1;
            let list: Folder[] = [];
            
            while(true){
                const resp = await vimeoClient.get<ListResponse<Folder>>(
                    `/api/vimeo/user/me/folders?page=${page}&per_page=100`
                );

                if(resp.data.data){
                    list.push(...resp.data.data);
                }

                if(!resp.data.paging.next){
                    break;
                }

                page++;
            }

            setFolders([...list]);
        }

        getFolders().catch(console.error);
    }, []);

    useEffect(() => {
        const nodeMap: Record<string, Node> = {};
        const topLevelFolders: string[] = [];

        function traverse(folders: Folder[]) {
            folders.forEach((f) => {
                const id = f.uri;
                // Skip the "Private To Me" folder if it is returned
                // in the API response. At one point, it was being
                // returned in the response but not anymore so this
                // is just a safeguard.
                if (
                    f.is_private_to_user &&
                    f.name.toLowerCase() === "private to me"
                ) {
                    return;
                }

                if (!nodeMap[id]) {
                    nodeMap[id] = {
                        id,
                        label: f.name,
                        type: "item",
                    };
                } else {
                    // This folder may have been added to the map previously because
                    // it was encountered as the parent folder of another folder.
                    // Simply set its label.
                    nodeMap[id].label = f.name;
                }

                if (!f.metadata.connections.parent_folder) {
                    topLevelFolders.push(f.uri);
                    return;
                }

                const parentFolder = f.metadata.connections.parent_folder;
                if (nodeMap[parentFolder.uri]) {
                    let parentFolderNodes = nodeMap[parentFolder.uri].nodes;
                    nodeMap[parentFolder.uri].type = "branch";
                    if (!parentFolderNodes) {
                        parentFolderNodes = [f.uri];
                    } else {
                        parentFolderNodes.push(f.uri);
                    }
                    nodeMap[parentFolder.uri].nodes = parentFolderNodes;
                } else {
                    nodeMap[parentFolder.uri] = {
                        id: parentFolder.uri,
                        // Set an empty label now. When we encounter
                        // the parent folder in a later iteration,
                        // we'll correct the label.
                        label: "",
                        type: "branch",
                        // Add the current folder as a child node.
                        nodes: [f.uri],
                    };
                }
            });
        }

        traverse(folders);

        const homeFolder: Node = {
            id: homeFolderId,
            label: "Videos",
            type: "item",
            selected: true,
        };
        nodeMap[homeFolderId] = homeFolder;

        // Add all top-level parent folders as child nodes to
        // a fake root node with id "0", basically a known id
        // that we can start from.
        nodeMap["0"] = {
            id: "0",
            nodes: [homeFolderId, ...topLevelFolders],
        };
        setNodes(nodeMap);
        // Set the first folder as the selected node,
        // which is typically the Private To Me folder.
        const node = nodeMap[nodeMap["0"].nodes![0]];
        setSelectedNode(node);
    }, [folders]);

    useEffect(() => {
        if (!selectedNode) {
            return;
        }

        props.folderSelected(
            selectedNode.id,
            folders?.find((f) => f.uri === selectedNode.id)?.metadata,
            selectedNode.id.includes("root")
        );
    });

    function getNodes(node: Node) {
        return node.nodes ? node.nodes.map((id: string) => nodes[id]) : [];
    }

    function onExpandClick(event: any, data: NodeExpandEvent) {
        const expandedNode: Record<string, Node> = {};
        expandedNode[data.node.id] = {
            ...data.node,
            expanded: data.expand,
        };
        setNodes({ ...nodes, ...expandedNode });
    }

    function onClick(event: any, data: NodeClickEvent) {
        // If there was a previously selected node, deselect that first.
        const previouslySelectedNode = selectedNode;
        if (previouslySelectedNode) {
            setNodes({
                ...nodes,
                ...{
                    [previouslySelectedNode.id]: {
                        ...previouslySelectedNode,
                        selected: false,
                    },
                },
                ...{
                    [data.node.id]: {
                        ...data.node,
                        selected: true,
                    },
                },
            });
        } else {
            setNodes({
                ...nodes,
                ...{
                    [data.node.id]: {
                        ...data.node,
                        selected: true,
                    },
                },
            });
        }

        // Replace the newly selected node to the state for easier tracking.
        setSelectedNode(data.node);
    }

    const tree = (
        <>
            <Tree
                id="folder-tree"
                getNodes={getNodes}
                nodes={nodes["0"].nodes}
                onExpandClick={onExpandClick}
                onClick={onClick}
            />
        </>
    );

    return (
        <>
            {folders && folders.length > 0 ? (
                <Popover
                    align="bottom"
                    hasNoNubbin={true}
                    style={{ marginTop: "8px", marginLeft: "10px" }}
                    body={tree}
                    heading="Folders"
                >
                    <Button
                        format="secondary"
                        icon={<FolderIcon />}
                        iconPosition="left"
                    >
                        {selectedNode?.label}
                        <ChevronDown />
                    </Button>
                </Popover>
            ) : (
                "Loading folders..."
            )}
        </>
    );
}
